import { useState, useEffect, useMemo, useCallback } from 'react';
import { subscribeGastosCompras, deleteGastoCompra } from '../../../../../firebase/facturacionUtils';
import { useTableState } from '../../../TablerosGestion/hooks/useTableState'; // Ajusta la ruta relativa a useTableState
import { sanitizeAmount } from '../../helpers';

export const useGastosComprasTable = () => {
    const [gastos, setGastos] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modales
    const [showModal, setShowModal] = useState(false);
    const [editingGasto, setEditingGasto] = useState(null);
    const [gastoToDelete, setGastoToDelete] = useState(null);
    const [gastoToView, setGastoToView] = useState(null);

    // 1. Suscripción en tiempo real a Firestore
    useEffect(() => {
        setLoading(true);
        const unsubscribe = subscribeGastosCompras((data) => {
            setGastos(data || []);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // 2. Mapeo e Hidratación de datos procesados
    const itemsTable = useMemo(() => {
        return gastos.map(g => {
            const esImpuesto = g.tipoComprobante === 'Impuestos / Tasas Fiscales';
            const moneda = g.moneda || 'ARS';

            // Cálculo de egreso real en banco considerando planes de pago o cheques
            let egresoEfectivoBanco = 0;
            const tienePlan = g.esPlanPago && Array.isArray(g.cuotasPlan) && g.cuotasPlan.length > 0;
            const tieneCheques = Array.isArray(g.chequesList) && g.chequesList.length > 0;

            if (tienePlan || tieneCheques) {
                const pagosDirectos = (Array.isArray(g.pagosList) ? g.pagosList : [])
                    .filter(p => p.formaPago !== 'Cheque')
                    .reduce((acc, p) => acc + sanitizeAmount(p.monto), 0);

                const chequesCobrados = (Array.isArray(g.chequesList) ? g.chequesList : [])
                    .filter(chk => chk.cobrado)
                    .reduce((acc, chk) => acc + sanitizeAmount(chk.monto), 0);

                const cuotasPagadas = (Array.isArray(g.cuotasPlan) ? g.cuotasPlan : [])
                    .filter(c => c.pagada)
                    .reduce((acc, c) => acc + sanitizeAmount(c.monto), 0);

                egresoEfectivoBanco = Math.round((pagosDirectos + chequesCobrados + cuotasPagadas) * 100) / 100;
            } else {
                egresoEfectivoBanco = sanitizeAmount(g.montoEgresoBanco);
            }

            const tieneRegistroBanco = g.montoEgresoBanco !== undefined && g.montoEgresoBanco !== '';
            const diferencia = g.diferenciaEgreso || 0;

            const esConciliado = tieneRegistroBanco && diferencia === 0;
            const esFaltante = tieneRegistroBanco && diferencia > 0;
            const esExcedente = tieneRegistroBanco && diferencia < 0;
            const tieneDesfasaje = esFaltante || esExcedente;

            const montoTotal = sanitizeAmount(g.montoTotal || g.gastoFinal);
            const montoNeto = sanitizeAmount(g.montoNeto);
            const montoIva = esImpuesto ? 0 : sanitizeAmount(g.montoIva);

            return {
                ...g,
                esImpuesto,
                moneda,
                montoNeto,
                montoIva,
                montoTotal,
                egresoEfectivoBanco,
                tieneRegistroBanco,
                diferencia,
                esConciliado,
                esFaltante,
                esExcedente,
                tieneDesfasaje,
                proveedorNombreDisplay: (g.proveedorNombre || 'Sin Proveedor').toUpperCase(),
                conceptoDisplay: (g.conceptoNombre || g.conceptoGastoNombre || 'Sin concepto').toUpperCase(),
                subconceptoDisplay: g.subconceptoNombre || 'S/D',
                areaNombreDisplay: g.areaNombre || 'S/A',
                tipoComprobanteDisplay: g.tipoComprobante || g.tipoComprobanteGasto || 'Comprobante',
                numeroComprobanteDisplay: g.numeroComprobante || 'S/N',
                periodoDisplay: g.periodo || '-'
            };
        });
    }, [gastos]);

    // 3. Instancia de useTableState
    const tableState = useTableState({
        data: itemsTable,
        initialSort: { key: 'fechaGasto', direction: 'descending' },
        initialColumns: {
            fechaGasto: true,
            fechaVencimientoGasto: true,
            numeroComprobanteDisplay: true,
            proveedorNombreDisplay: true,
            conceptoDisplay: true,
            subconceptoDisplay: true,
            areaNombreDisplay: true,
            montoNeto: true,
            montoIva: true,
            montoTotal: true,
            egresoEfectivoBanco: true,
            estatus: true
        },
        initialFilters: {},
        recordsPerPage: 100
    });

    const handleOpenCreate = useCallback(() => {
        setEditingGasto(null);
        setShowModal(true);
    }, []);

    const handleOpenEdit = useCallback((gasto) => {
        setEditingGasto(gasto);
        setShowModal(true);
    }, []);

    const handleDelete = useCallback(async () => {
        if (!gastoToDelete) return;
        try {
            await deleteGastoCompra(gastoToDelete.id);
            setGastoToDelete(null);
        } catch (error) {
            console.error("Error al eliminar gasto:", error);
        }
    }, [gastoToDelete]);

    return {
        state: {
            loading,
            showModal,
            editingGasto,
            gastoToDelete,
            gastoToView,
            itemsTable
        },
        actions: {
            setShowModal,
            setGastoToDelete,
            setGastoToView,
            handleOpenCreate,
            handleOpenEdit,
            handleDelete
        },
        tableState
    };
};